import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./TraceryPaywall.module.scss"

// ─────────────────────────────────────────────────────────────────────────────
// "Tracery" — an EXPIRATION / win-back paywall built on ENDOWMENT + CONTINUITY
// + CONTRAST, told through the app's own real security gesture: your unlock
// PATTERN. Premium lapsed, so right now your memories are held by your passcode
// ALONE — perfectly safe, but the full watch is off. Nine of your memories sit
// as lit lenses in a 3×3 constellation; you TRACE your pattern across them and a
// luminous tracery links lens to lens. As the pattern closes, your four real
// Premium protections come back on watch and the whole constellation arms.
//
// The interaction is a genuine pattern-lock TRACE (drag / tap / keyboard through
// the nodes in sequence) — not a wax seal, a slider, a plug, a roller, a card
// reader, a power switch or an ECG. The control IS the hero and it is the app's
// actual security affordance. Emotional register: calm, capable reassurance —
// "draw your guard back on" — not fear or countdowns.
//
// Honest & App-Store compliant: the featured, most-prominent price is the yearly
// plan (7-day free trial, per-month broken out, real Save %); monthly is a small
// secondary line. No countdown, no scarcity, no data-loss claim — the pattern
// only decides who ELSE can get in; your photos are never deleted or locked away
// from you. Reuses only the house font, colour tokens and bundled memory photos.
// Self-contained: renders on debug data, no native injection required.
// ─────────────────────────────────────────────────────────────────────────────

const PUB = process.env.PUBLIC_URL ?? ""
const photo = (n: string) => `${PUB}/byheart/${n}.jpg`

// Nine memories, one per lens of the 3×3 constellation (index 0-8, row-major).
const LENSES = [
  "m05",
  "m12",
  "m02",
  "m09",
  "m14",
  "m07",
  "m03",
  "m11",
  "m06",
].map(photo)

// The pattern spirals from a corner inward to the centre — closing in to secure
// the core. Node centres live in a 0–100 square space (a uniform 3×3 grid at
// 20/50/80%), so the trace SVG needs no pixel measurement.
const PATH = [0, 1, 2, 5, 8, 7, 6, 3, 4]
const AXIS = [20, 50, 80]
const nodePos = (i: number) => ({ x: AXIS[i % 3], y: AXIS[Math.floor(i / 3)] })
const CAPTURE_R = 15 // capture radius in 0-100 units

// Real Premium protections. `at` = how many nodes must be traced before this one
// comes back on watch, so they light in sequence as the pattern closes.
const TOKENS: { label: string; at: number; icon: JSX.Element }[] = [
  {
    label: "Only-you unlock",
    at: 3,
    icon: (
      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
        <path
          d="M7 10V7.4a5 5 0 0 1 10 0V10"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <rect
          x="4.8"
          y="10"
          width="14.4"
          height="10"
          rx="2.6"
          fill="currentColor"
        />
        <circle cx="12" cy="14.6" r="1.5" fill="var(--tr-lens-hole)" />
      </svg>
    ),
  },
  {
    label: "Encrypted backup",
    at: 5,
    icon: (
      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
        <path
          d="M12 3.4l7 3v5c0 4.6-3 7.6-7 9-4-1.4-7-4.4-7-9v-5l7-3z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
        <path
          d="M8.7 12.2l2.2 2.2 4.4-4.6"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    label: "Break-in reports",
    at: 7,
    icon: (
      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
        <path
          d="M2.5 12S6 5.6 12 5.6 21.5 12 21.5 12 18 18.4 12 18.4 2.5 12 2.5 12Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
        <circle
          cx="12"
          cy="12"
          r="2.7"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        />
      </svg>
    ),
  },
  {
    label: "Private sync, every device",
    at: 9,
    icon: (
      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
        <path
          d="M5 13a7 7 0 0 1 12.3-4.5M19 11a7 7 0 0 1-12.3 4.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <path
          d="M17 4.5V8h-3.5M7 19.5V16h3.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
]

const dist = (ax: number, ay: number, bx: number, by: number) =>
  Math.hypot(ax - bx, ay - by)

function TraceryPaywall() {
  const { state, actions } = useAppState()
  const reduce = useReducedMotion()

  const topInset = state.initialData?.topInset ?? 0
  const bottomInset = state.initialData?.bottomInset ?? 0
  const darkMode = state.initialData?.darkMode ?? false

  const premium = state.productData?.premium
  const yearly = premium?.yearlyProduct
  const monthly = premium?.monthlyProduct
  const locale = yearly?.priceLocale

  const yearlyPrice = yearly ? formatCurrency(yearly.price, locale) : ""
  const perMonth = yearly ? formatCurrency(yearly.price / 12, locale) : ""
  const monthlyPrice = monthly ? formatCurrency(monthly.price, locale) : ""
  const savings = useMemo(() => {
    if (!yearly || !monthly) return 0
    return Math.round((1 - yearly.price / (monthly.price * 12)) * 100)
  }, [yearly, monthly])

  // visited = how many nodes of PATH are traced; armed once the pattern closes.
  const [visited, setVisited] = useState(reduce ? PATH.length : 0)
  const [armed, setArmed] = useState(reduce ? true : false)
  const [pointer, setPointer] = useState<{ x: number; y: number } | null>(null)
  const [tracing, setTracing] = useState(false)
  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")
  const [showClose, setShowClose] = useState(false)
  const [committed, setCommitted] = useState(false)

  const visitedRef = useRef(visited)
  const armedRef = useRef(armed)
  const touchedRef = useRef(false)
  const surfaceRef = useRef<HTMLDivElement | null>(null)
  const timers = useRef<number[]>([])
  const push = (fn: () => void, ms: number) =>
    timers.current.push(window.setTimeout(fn, ms))

  // Warm the safe-area bars to the night-watch ground so there are no cold bars.
  useEffect(() => {
    actions.setIgnoreHeaderInset(true)
    actions.setIgnoreFooterInset(true)
    actions.setHeaderInsetColor(darkMode ? "#0f1320" : "#eef1f8")
    actions.setFooterInsetColor(darkMode ? "#0b0e18" : "#e6ebf5")
  }, [actions, darkMode])

  const setVisitedTo = useCallback((n: number) => {
    visitedRef.current = n
    setVisited(n)
    if (n >= PATH.length && !armedRef.current) {
      armedRef.current = true
      setArmed(true)
      native.logMixpanelEvent("html_tracery_armed")
    }
  }, [])

  // Capture the next expected node(s) if the pointer is close enough.
  const captureFrom = useCallback(
    (px: number, py: number) => {
      let n = visitedRef.current
      while (n < PATH.length) {
        const p = nodePos(PATH[n])
        if (dist(px, py, p.x, p.y) <= CAPTURE_R) n += 1
        else break
      }
      if (n !== visitedRef.current) setVisitedTo(n)
    },
    [setVisitedTo],
  )

  const toLocal = (clientX: number, clientY: number) => {
    const rect = surfaceRef.current?.getBoundingClientRect()
    if (!rect) return null
    return {
      x: ((clientX - rect.left) / rect.width) * 100,
      y: ((clientY - rect.top) / rect.height) * 100,
    }
  }

  const takeOver = () => {
    touchedRef.current = true
  }

  const onPointerDown = (e: React.PointerEvent) => {
    if (armedRef.current) return
    e.currentTarget.setPointerCapture?.(e.pointerId)
    takeOver()
    setTracing(true)
    const loc = toLocal(e.clientX, e.clientY)
    if (loc) {
      setPointer(loc)
      captureFrom(loc.x, loc.y)
    }
  }
  const onPointerMove = (e: React.PointerEvent) => {
    if (!tracing || armedRef.current) return
    const loc = toLocal(e.clientX, e.clientY)
    if (loc) {
      setPointer(loc)
      captureFrom(loc.x, loc.y)
    }
  }
  const endTrace = () => {
    setTracing(false)
    setPointer(null)
  }
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== " " && e.key !== "Enter" && e.key !== "ArrowRight") return
    e.preventDefault()
    if (armedRef.current) return
    takeOver()
    setPointer(null)
    setVisitedTo(Math.min(PATH.length, visitedRef.current + 1))
  }

  // On load: reveal the close affordance, then auto-trace the pattern so passive
  // viewers and screenshots land on the finished, armed constellation. If the
  // user grabs it first, the auto-trace stands down.
  useEffect(() => {
    push(() => setShowClose(true), reduce ? 0 : 1400)
    if (reduce) return () => timers.current.forEach(window.clearTimeout)
    const step = (i: number) => {
      if (touchedRef.current || armedRef.current) return
      setVisitedTo(i)
      if (i < PATH.length) push(() => step(i + 1), 240)
    }
    push(() => step(1), 900)
    return () => timers.current.forEach(window.clearTimeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduce])

  const purchase = useCallback(() => {
    const product = plan === "yearly" ? yearly : monthly
    setCommitted(true)
    native.logMixpanelEvent("html_tracery_purchase", { plan })
    push(() => {
      if (product) native.subscribe(product)
    }, 700)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan, yearly, monthly])

  const handleClose = () => native.postNativeEvent("close")

  // Trace geometry in 0-100 space.
  const linePts = PATH.slice(0, visited).map(nodePos)
  const lineD = linePts.map((p, i) => `${i ? "L" : "M"}${p.x} ${p.y}`).join(" ")
  const last = linePts[linePts.length - 1]
  const showBand =
    tracing && !armed && !!last && !!pointer && visited < PATH.length

  const progress = visited / PATH.length
  const readout = armed
    ? "Back on watch — your full guard is on"
    : visited > 0
      ? `Tracing your pattern — ${visited} of ${PATH.length}`
      : "Held by your passcode alone right now"

  const ctaSub =
    plan === "yearly"
      ? `${yearlyPrice}/year after your 7-day free trial — just ${perMonth}/mo. Renews yearly, cancel anytime.`
      : `${monthlyPrice}/month. Renews monthly, cancel anytime.`

  return (
    <div
      className={`${styles.root} ${darkMode ? styles.isDark : ""} ${
        armed ? styles.isArmed : ""
      } ${committed ? styles.isCommitted : ""}`}
      style={{ ["--p" as any]: progress }}
    >
      <div className={styles.ground} aria-hidden>
        <span className={styles.aura} />
        <span className={styles.grain} />
      </div>

      <div
        className={styles.scroll}
        style={{ paddingTop: topInset + 12, paddingBottom: bottomInset + 14 }}
      >
        {/* Top bar — brand clarity */}
        <div className={styles.topbar}>
          <span className={styles.wordmark}>
            <span className={styles.wmMark} aria-hidden>
              <svg viewBox="0 0 24 24" width="12" height="12">
                <path
                  d="M8 10V7a4 4 0 0 1 8 0v3"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <rect
                  x="5.5"
                  y="10"
                  width="13"
                  height="9.5"
                  rx="2.4"
                  fill="currentColor"
                />
              </svg>
            </span>
            PRIVATE&nbsp;PHOTO&nbsp;VAULT
          </span>
          <button
            className={`${styles.close} ${showClose ? styles.closeOn : ""}`}
            onClick={handleClose}
            aria-label="Not now"
            tabIndex={showClose ? 0 : -1}
          >
            <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Headline */}
        <header className={styles.hero}>
          <p className={styles.eyebrow}>
            <span className={styles.eyebrowDot} aria-hidden />
            Premium protection is paused
          </p>
          <h1 className={styles.title}>
            Draw your guard
            <br />
            <span className={styles.titleThen}>back on watch.</span>
          </h1>
        </header>

        {/* ── The pattern-trace constellation ──────────────────────────────── */}
        <section className={styles.stageWrap} aria-label="Trace your pattern">
          <div
            ref={surfaceRef}
            className={`${styles.surface} ${tracing ? styles.surfaceLive : ""}`}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endTrace}
            onPointerCancel={endTrace}
            onKeyDown={onKeyDown}
            role="application"
            aria-label="Trace your unlock pattern across your memories to bring your guard back on watch"
            tabIndex={0}
          >
            {/* the nine memory lenses */}
            <div className={styles.grid} aria-hidden>
              {LENSES.map((src, i) => {
                const order = PATH.indexOf(i)
                const lit = order > -1 && order < visited
                const pos = nodePos(i)
                return (
                  <span
                    key={i}
                    className={`${styles.lens} ${lit ? styles.lensLit : ""}`}
                    style={{
                      left: `${pos.x}%`,
                      top: `${pos.y}%`,
                      ["--i" as any]: order > -1 ? order : 0,
                    }}
                  >
                    <img src={src} alt="" draggable={false} loading="eager" />
                    <span className={styles.lensRing} />
                    <span className={styles.lensDot} />
                  </span>
                )
              })}
            </div>

            {/* the luminous tracery */}
            <svg
              className={styles.trace}
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              aria-hidden
            >
              {lineD && (
                <>
                  <path className={styles.traceGlow} d={lineD} />
                  <path className={styles.traceLine} d={lineD} />
                </>
              )}
              {showBand && last && pointer && (
                <line
                  className={styles.band}
                  x1={last.x}
                  y1={last.y}
                  x2={pointer.x}
                  y2={pointer.y}
                />
              )}
            </svg>

            {/* hint shown before the first trace */}
            {visited === 0 && !tracing && (
              <span className={styles.hint}>
                <span className={styles.hintPulse} aria-hidden />
                Trace to re-arm
              </span>
            )}
          </div>

          {/* live readout */}
          <div className={styles.readout} aria-live="polite">
            {armed ? (
              <span className={styles.readArmed}>
                <span className={styles.readDot} />
                {readout}
              </span>
            ) : (
              <span className={styles.readOpen}>{readout}</span>
            )}
          </div>

          {/* real protections — each comes back on watch as the pattern closes */}
          <ul
            className={styles.tokens}
            aria-label="What your pattern brings back"
          >
            {TOKENS.map((tk) => (
              <li
                key={tk.label}
                className={`${styles.token} ${
                  visited >= tk.at ? styles.tokenLit : ""
                }`}
              >
                <span className={styles.tokenOrb}>{tk.icon}</span>
                <span className={styles.tokenLabel}>{tk.label}</span>
                <span className={styles.tokenState} aria-hidden>
                  {visited >= tk.at ? "on watch" : "paused"}
                </span>
              </li>
            ))}
          </ul>
        </section>

        {/* ── Plans — yearly is the hero ─────────────────────────────────── */}
        <section className={styles.plans} aria-label="Choose your plan">
          <button
            className={`${styles.plan} ${styles.planYear} ${
              plan === "yearly" ? styles.planOn : ""
            }`}
            onClick={() => setPlan("yearly")}
            aria-pressed={plan === "yearly"}
          >
            {savings > 0 && (
              <span className={styles.bestTag}>Save {savings}%</span>
            )}
            <span className={styles.planMain}>
              <span className={styles.radio} aria-hidden>
                <span className={styles.radioDot} />
              </span>
              <span className={styles.planTextWrap}>
                <span className={styles.planName}>On watch all year</span>
                <span className={styles.planCovers}>
                  7-day free trial, then billed yearly
                </span>
              </span>
              <span className={styles.planPrice}>
                <span className={styles.priceRow}>
                  <span className={styles.priceBig}>{yearlyPrice}</span>
                  <span className={styles.pricePer}>/yr</span>
                </span>
                <span className={styles.pricePm}>{perMonth}/mo</span>
              </span>
            </span>
          </button>

          <button
            className={`${styles.plan} ${styles.planMonth} ${
              plan === "monthly" ? styles.planOn : ""
            }`}
            onClick={() => setPlan("monthly")}
            aria-pressed={plan === "monthly"}
          >
            <span className={styles.planMain}>
              <span className={styles.radio} aria-hidden>
                <span className={styles.radioDot} />
              </span>
              <span className={styles.planTextWrap}>
                <span className={styles.planNameSm}>Monthly</span>
                <span className={styles.planCoversSm}>one month at a time</span>
              </span>
              <span className={styles.planPriceSm}>
                <span className={styles.priceSm}>{monthlyPrice}</span>
                <span className={styles.pricePerSm}>/mo</span>
              </span>
            </span>
          </button>
        </section>

        {/* CTA */}
        <div className={styles.ctaDock}>
          <button
            className={`${styles.cta} ${committed ? styles.ctaCommitted : ""}`}
            onClick={purchase}
          >
            <span className={styles.ctaLabel}>
              {committed
                ? "Bringing your guard back…"
                : "Reactivate Premium — 7 days free"}
            </span>
            {!committed && (
              <svg
                className={styles.ctaArrow}
                viewBox="0 0 24 24"
                width="18"
                height="18"
                aria-hidden
              >
                <path
                  d="M5 12h13M12 5l7 7-7 7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
          <p className={styles.ctaSub}>{ctaSub}</p>
          <p className={styles.reassure}>
            Your photos never left — they're still right here, guarded by your
            passcode. Premium just brings the full watch back on. Nothing is
            ever deleted or locked away from you.
          </p>

          <div className={styles.legal}>
            <button onClick={() => native.postNativeEvent("already_purchased")}>
              Restore
            </button>
            <span>·</span>
            <button onClick={() => native.postNativeEvent("terms")}>
              Terms
            </button>
            <span>·</span>
            <button onClick={() => native.postNativeEvent("privacy")}>
              Privacy
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TraceryPaywall
